/**
 * Visual tokens shared by the three Reportes v3 pages (Plagas · Costes · Campañas).
 *
 * Single source for the card chrome, the solid icon tile, the series colours and
 * the amber pressure ramp so every section — whichever agent wrote it — looks
 * like the same product. Text NEVER takes a series colour: identity colours
 * live on dots, swatches and bars beside black ink.
 */

/** White rounded card used by every report section / KPI tile. */
export const REPORT_CARD_CLASS =
  "rounded-2xl border border-[#E4EDF5] bg-white shadow-[0_4px_16px_rgba(20,60,100,0.05)]";

/** Tiny uppercase label above numbers. */
export const REPORT_LABEL_CLASS = "text-[11px] font-bold uppercase tracking-wider text-muted-foreground";

/** Big hero number — black ink, never coloured. */
export const REPORT_BIG_NUMBER_CLASS = "text-2xl sm:text-3xl font-bold text-foreground leading-tight";

/** Section title / question line. */
export const REPORT_SECTION_TITLE_CLASS = "text-[15px] font-bold text-foreground leading-tight";
export const REPORT_QUESTION_CLASS = "text-xs text-muted-foreground leading-snug";

/** Focus ring applied to every interactive element in the shell. */
export const REPORT_FOCUS_RING_CLASS =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** Report accents (solid icon tile + section accents). */
export const REPORT_ACCENTS = {
  plagas: "#E0962E",
  costes: "#2E84DE",
  campanas: "#059669",
} as const;

export type ReportKey = keyof typeof REPORT_ACCENTS;

/** Inline style of the solid icon tile (`w-9 h-9 rounded-xl text-white`). */
export function tileStyle(accent: string): { backgroundColor: string; boxShadow: string } {
  return {
    backgroundColor: accent,
    boxShadow: `0 6px 14px ${accent}42, inset 0 1px 0 rgba(255,255,255,0.28)`,
  };
}

/** Series colours for charts. */
export const SERIES_COLORS = {
  biological: "hsl(177,58%,52%)",
  chemical: "hsl(25,95%,53%)",
  unclassified: "#9AA8B6",
  /** Previous period / ghost bar. */
  ghost: "#C9D3DD",
  /** A vs B: B is always grey; A takes the report accent. */
  compareB: "#9AA8B6",
} as const;

/** Ordinal severity ramp (baja → crítica) + «sin severidad». */
export const SEVERITY_COLORS = {
  low: "#F2D2A0",
  medium: "#E8B36A",
  high: "#D9842A",
  critical: "#A84E12",
  unknown: "#C9D3DD",
} as const;

/** Semantic good / bad / neutral (text on soft background; solid pill hex). */
export const SEMANTIC = {
  good: { text: "text-emerald-700", bg: "bg-emerald-50", pill: "#16A971" },
  bad: { text: "text-rose-600", bg: "bg-rose-50", pill: "#E5574E" },
  neutral: { text: "text-muted-foreground", bg: "bg-[#F0F4F8]", pill: "#64748B" },
} as const;

/** Amber ramp by pressure (0..1) used by the sector tile grid and heat cells. */
export const PRESSURE_RAMP: ReadonlyArray<{ max: number; bg: string; ink: string }> = [
  { max: 0, bg: "#F0F4F8", ink: "#13314A" },
  { max: 0.25, bg: "#F2D2A0", ink: "#13314A" },
  { max: 0.5, bg: "#E8B36A", ink: "#13314A" },
  { max: 0.75, bg: "#D9842A", ink: "#FFFFFF" },
  { max: 1, bg: "#A84E12", ink: "#FFFFFF" },
];

/**
 * Background + ink colour for a pressure value in 0..1 (values are clamped;
 * NaN counts as 0 → neutral tile).
 */
export function pressureTileColor(pressure: number): { bg: string; ink: string } {
  const p = Number.isFinite(pressure) ? Math.min(1, Math.max(0, pressure)) : 0;
  for (const step of PRESSURE_RAMP) {
    if (p <= step.max) return { bg: step.bg, ink: step.ink };
  }
  const last = PRESSURE_RAMP[PRESSURE_RAMP.length - 1];
  return { bg: last.bg, ink: last.ink };
}

/** Recharts grid props shared by every chart. */
export const CHART_GRID_PROPS = {
  strokeDasharray: "2 4",
  stroke: "hsl(215 20% 90%)",
  vertical: false,
} as const;

/** Recharts axis props shared by every chart (abbreviated ticks, no lines). */
export const CHART_AXIS_PROPS = {
  axisLine: false,
  tickLine: false,
  tick: { fontSize: 10 },
} as const;

/** Recharts bar props shared by every chart. */
export const CHART_BAR_PROPS = {
  maxBarSize: 22,
  radius: [10, 10, 10, 10] as [number, number, number, number],
} as const;
